// WRAITH echoes: translucent violet copies of the mech that replay the player's
// shots from their own position, aimed at the same world point, at reduced power.
// Phase Echo leaves one standing at the activation point; Eclipse spins four in
// orbit around WRAITH. The body is a real MechView when the player module
// provides one, otherwise a procedural silhouette (never crashes).

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, MechViewApi, ProjectileSpec, Vec } from '../../core/contracts';
import { createMechView } from '../player/MechView';
import { replayAngle } from './math';
import { ATEX, texOr, type KitHost } from './shared';

/** Violet multiply tint applied to every sprite of an echo's MechView. */
const ECHO_TINT = 0xc7a2ff;
/** Seconds of fade-in / fade-out. */
const FADE_IN = 0.18;
const FADE_OUT = 0.45;
/** Beam weapons: the echo zaps the nearest enemy within this range. */
const ZAP_RANGE = 560;
const ZAP_DAMAGE = 10;

export interface EchoOptions {
  x: number;
  feetY: number;
  facing: 1 | -1;
  /** Seconds. */
  duration: number;
  /** Projectile damage multiplier for replays. */
  damageMult: number;
  scale?: number;
  alpha?: number;
  /** Orbit WRAITH instead of standing still (Eclipse). */
  orbit?: { phase: number; radius: number; speed: number };
}

/** A volley captured from weapon:fired (specs are cloned so later mutation cannot leak in). */
export interface Volley {
  x: number;
  y: number;
  angle: number;
  specs: ProjectileSpec[];
  target: Vec;
}

export class Echo {
  x: number;
  feetY: number;
  private facing: 1 | -1;
  private age = 0;
  private dying = false;
  /** Age at which the fade-out started. */
  private dyingAt = 0;
  private dead = false;
  private readonly baseAlpha: number;
  private readonly scale: number;
  private view: MechViewApi | null = null;
  private ghost: Phaser.GameObjects.Container | null = null;
  private readonly glow: Phaser.GameObjects.Image;
  private aimAngle = 0;

  constructor(
    private readonly h: KitHost,
    private readonly opts: EchoOptions,
  ) {
    this.x = opts.x;
    this.feetY = opts.feetY;
    this.facing = opts.facing;
    this.baseAlpha = opts.alpha ?? 0.5;
    this.scale = opts.scale ?? 1;
    const scene = h.ctx.scene;

    this.glow = h.track(scene.add.image(this.x, this.feetY - 48 * this.scale, texOr(scene, TEX.soft, ATEX.soft)));
    this.glow
      .setTint(COLORS.wraith)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(DEPTH.PLAYER - 2)
      .setScale(2.4 * this.scale, 3 * this.scale)
      .setAlpha(0);

    this.view = this.buildView();
    if (!this.view) this.ghost = this.buildGhost();
    if (opts.orbit) this.placeOrbit(0);
    this.applyVisual(0);
  }

  get alive(): boolean {
    return !this.dead && !this.dying;
  }

  /** Fully faded and destroyed. */
  get gone(): boolean {
    return this.dead;
  }

  get damageMult(): number {
    return this.opts.damageMult;
  }

  /** World muzzle the echo fires from. */
  muzzle(): { x: number; y: number } {
    if (this.view) {
      try {
        const m = this.view.muzzleWorld();
        if (Number.isFinite(m.x) && Number.isFinite(m.y)) return m;
      } catch {
        // fall back below
      }
    }
    return { x: this.x + this.facing * 34 * this.scale, y: this.feetY - 58 * this.scale };
  }

  update(dt: number): void {
    if (this.dead) return;
    this.age += dt;
    if (this.opts.orbit) this.placeOrbit(dt);

    // Echoes look where the player is aiming.
    const ptr = this.h.ctx.input.pointerWorld;
    const m = this.muzzle();
    this.aimAngle = Math.atan2(ptr.y - m.y, ptr.x - m.x);
    const f: 1 | -1 = ptr.x >= this.x ? 1 : -1;
    this.facing = f;

    if (!this.dying && this.age >= this.opts.duration) this.dismiss();
    if (this.dying && this.age >= this.dyingAt + FADE_OUT) {
      this.destroy();
      return;
    }
    this.applyVisual(dt);
  }

  /** Re-fire a captured volley from this echo. */
  replay(v: Volley): void {
    if (!this.alive) return;
    const { ctx, safe } = this.h;
    const m = this.muzzle();
    if (v.specs.length === 0) {
      // Beam / hitscan weapon: zap the nearest enemy instead.
      const target = safe.call('enemies.nearest', () => ctx.enemies.nearest(m.x, m.y, ZAP_RANGE), null as EnemyApi | null);
      if (!target) return;
      const opts = { count: 0, range: 0, damage: ZAP_DAMAGE, tags: ['electric' as const], color: COLORS.wraithGlow, secondary: true, label: 'phase_echo' };
      safe.run('damage.chainLightning', () => ctx.damage.chainLightning(m, target, opts));
      this.recoil(Math.atan2(target.y - m.y, target.x - m.x), 0.3);
      return;
    }
    const mult = this.opts.damageMult;
    let angle = v.angle;
    for (const s of v.specs) {
      angle = replayAngle(s.angle, v.angle, m.x, m.y, v.target);
      const spec: ProjectileSpec = {
        ...s,
        owner: 'player',
        x: m.x + (s.x - v.x),
        y: m.y + (s.y - v.y),
        angle,
        damage: s.damage * mult,
        tint: COLORS.wraithGlow,
        glow: true,
        secondary: true,
        explode: s.explode ? { ...s.explode, damage: s.explode.damage * mult } : undefined,
        field: s.field ? { ...s.field, implodeDamage: s.field.implodeDamage * mult } : undefined,
        explodeOnKill: s.explodeOnKill ? { ...s.explodeOnKill, damage: s.explodeOnKill.damage * mult } : undefined,
      };
      safe.run('projectiles.spawn', () => ctx.projectiles.spawn(spec));
    }
    this.recoil(angle, 0.6);
  }

  /** Begin the fade-out (the echo stops firing immediately). */
  dismiss(): void {
    if (this.dying || this.dead) return;
    this.dying = true;
    this.dyingAt = this.age;
    const { ctx, safe } = this.h;
    safe.run('fx.ring', () => ctx.fx.ring(this.x, this.feetY - 48 * this.scale, 56 * this.scale, { color: COLORS.wraith, width: 2, fromRadius: 20 }));
    safe.run('fx.sparks', () =>
      ctx.fx.sparks(this.x, this.feetY - 48 * this.scale, -Math.PI / 2, { count: 8, color: COLORS.wraithGlow, speed: 180, spread: Math.PI }),
    );
  }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    if (this.view) {
      try {
        this.h.untrack(this.view.root);
        this.view.destroy();
      } catch {
        // scene already shut down
      }
      this.view = null;
    }
    if (this.ghost) {
      this.h.untrack(this.ghost);
      this.ghost.destroy();
      this.ghost = null;
    }
    this.h.untrack(this.glow);
    this.glow.destroy();
  }

  // ───────────── internals ─────────────

  private recoil(angle: number, amount: number): void {
    const { ctx, safe } = this.h;
    const m = this.muzzle();
    safe.run('fx.muzzleFlash', () => ctx.fx.muzzleFlash(m.x, m.y, angle, COLORS.wraithGlow, 0.8 * this.scale));
    if (this.view) {
      const v = this.view;
      safe.run('echo.recoil', () => v.recoil(amount));
    }
  }

  private placeOrbit(dt: number): void {
    const o = this.opts.orbit!;
    o.phase += o.speed * dt;
    const p = this.h.ctx.player;
    // Flattened orbit around the torso; echoes behind the mech draw behind it.
    this.x = p.x + Math.cos(o.phase) * o.radius;
    this.feetY = p.feetY - 10 + Math.sin(o.phase) * o.radius * 0.38;
    const behind = Math.sin(o.phase) < 0;
    const depth = behind ? DEPTH.PLAYER - 1 : DEPTH.PLAYER + 1;
    if (this.view) this.view.setDepth(depth);
    if (this.ghost) this.ghost.setDepth(depth);
  }

  private applyVisual(dt: number): void {
    const fadeIn = Math.min(1, this.age / FADE_IN);
    const fadeOut = this.dying ? Math.max(0, 1 - (this.age - this.dyingAt) / FADE_OUT) : 1;
    // Unstable shimmer: a fast flicker plus a slow breathing pulse.
    const shimmer = 0.85 + 0.1 * Math.sin(this.age * 23) + 0.05 * Math.sin(this.age * 3.1);
    const a = this.baseAlpha * fadeIn * fadeOut * shimmer;
    const bob = this.opts.orbit ? 0 : Math.sin(this.age * 2.4) * 3;
    const s = this.scale * (this.dying ? 1 + (1 - fadeOut) * 0.15 : 1);

    this.glow.setPosition(this.x, this.feetY - 48 * this.scale + bob).setAlpha(0.35 * fadeIn * fadeOut);
    if (this.view) {
      const v = this.view;
      this.h.safe.run('echo.view', () => {
        v.setPosition(this.x, this.feetY + bob);
        v.setFacing(this.facing);
        v.setAim(this.aimAngle);
        v.setAlpha(a);
        v.setScale(s);
        v.update(dt);
      });
    } else if (this.ghost) {
      this.ghost.setPosition(this.x, this.feetY + bob).setAlpha(a).setScale(s * this.facing, s);
    }
  }

  private buildView(): MechViewApi | null {
    const { ctx } = this.h;
    let view: MechViewApi;
    try {
      view = createMechView(ctx.scene, 'wraith');
    } catch {
      return null;
    }
    try {
      view.setEquipment(ctx.run.inventory.visualIds());
      view.setWeapon(ctx.player.activeWeapon()?.def.id ?? null);
      view.setReactorColor(COLORS.wraithGlow);
      view.setDepth(DEPTH.PLAYER - 1);
      view.setPosition(this.x, this.feetY);
      view.setFacing(this.facing);
      view.play('idle');
      tintTree(view.root, ECHO_TINT);
      this.h.track(view.root);
    } catch {
      // A partially working view is still better than none; keep it.
    }
    return view;
  }

  /** Procedural silhouette used when MechView is unavailable. Drawn facing right, feet at (0, 0). */
  private buildGhost(): Phaser.GameObjects.Container {
    const scene = this.h.ctx.scene;
    const g = scene.add.graphics();
    const body = COLORS.wraith;
    const edge = COLORS.wraithGlow;
    g.fillStyle(body, 0.55);
    // Legs
    g.fillRoundedRect(-14, -46, 10, 46, 3);
    g.fillRoundedRect(4, -46, 10, 46, 3);
    // Pelvis + torso (narrow frame)
    g.fillRoundedRect(-16, -54, 32, 12, 4);
    g.fillTriangle(-20, -88, 20, -88, 0, -52);
    g.fillRoundedRect(-18, -94, 36, 16, 5);
    // Head + visor
    g.fillRoundedRect(-9, -112, 18, 16, 5);
    g.fillStyle(edge, 0.9);
    g.fillRect(-2, -106, 12, 3);
    // Floating shoulder fins
    g.fillStyle(body, 0.45);
    g.fillTriangle(-30, -98, -18, -92, -22, -74);
    g.fillTriangle(30, -98, 18, -92, 22, -74);
    // Arm + rifle pointing forward
    g.fillRoundedRect(8, -84, 34, 6, 2);
    g.fillStyle(edge, 0.85);
    g.fillRect(40, -83, 12, 3);
    // Reactor core
    g.fillCircle(0, -76, 4);
    g.lineStyle(1.5, edge, 0.8);
    g.strokeRoundedRect(-18, -94, 36, 16, 5);
    g.setBlendMode(Phaser.BlendModes.ADD);
    const c = scene.add.container(this.x, this.feetY, [g]);
    c.setDepth(DEPTH.PLAYER - 1);
    this.h.track(c);
    return c;
  }
}

/** Multiply-tint every tintable descendant of a container. */
function tintTree(obj: Phaser.GameObjects.GameObject, tint: number): void {
  const anyObj = obj as unknown as { list?: Phaser.GameObjects.GameObject[]; setTint?: (t: number) => void };
  if (Array.isArray(anyObj.list)) for (const child of anyObj.list) tintTree(child, tint);
  else if (typeof anyObj.setTint === 'function') anyObj.setTint(tint);
}

/** Snapshot a weapon:fired payload into a replayable volley. */
export function captureVolley(ev: { x: number; y: number; angle: number; specs: ProjectileSpec[] }, target: Vec): Volley {
  return {
    x: ev.x,
    y: ev.y,
    angle: ev.angle,
    target: { x: target.x, y: target.y },
    specs: ev.specs.map((s) => ({ ...s, tags: s.tags.slice(), statuses: s.statuses?.map((st) => ({ ...st })) })),
  };
}
